










































































/**
 * Copyright (c) Matthieu Jabbour. All Rights Reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import Vue from 'vue';
import { Generic } from 'scripts/vue/types';
import markdown from 'scripts/helpers/markdown';
import buildClass from 'scripts/helpers/buildClass';
import generateRandomId from 'scripts/helpers/generateRandomId';

interface Option {
  type: 'header' | 'divider' | 'option';
  value?: string;
  label?: string;
  disabled?: boolean;
}

interface Props {
  id: string;
  icon: string;
  label: string;
  multiple: boolean;
  helper: string;
  modifiers: string;
  name: string;
  value: string[];
  options: Option[];
}

type Mapping = Record<string, string>;

const findOption = (value: string) => (options: Option[]): number => (
  options.findIndex((option) => value === option.value && option.value !== undefined)
);

const generateMapping = (mapping: Mapping, option: Option): Mapping => (
  (option.value !== undefined && option.label !== undefined)
    ? ({ ...mapping, [option.value as string]: markdown(option.label as string) })
    : mapping
);

/**
 * Dropdown.
 */
export default Vue.extend<Generic, Generic, Generic, Props>({
  name: 'UIDropdown',
  props: {
    id: {
      type: String,
      default: null,
      required: false,
    },
    icon: {
      type: String,
      default: null,
      required: false,
    },
    label: {
      type: String,
      default: null,
      required: false,
    },
    helper: {
      type: String,
      default: null,
      required: false,
    },
    modifiers: {
      type: String,
      default: '',
      required: false,
    },
    multiple: {
      type: Boolean,
      default: false,
      required: false,
    },
    value: {
      type: Array,
      default: () => [],
      required: false,
    },
    name: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isDisplayed: false,
      position: 'bottom',
      currentValue: this.value,
      randomId: generateRandomId(),
      mapping: this.options.reduce(generateMapping, {}),
      focusedOption: findOption(this.value[0])(this.options),
    };
  },
  computed: {
    fieldValue(): string {
      return this.currentValue.map((option: string) => this.mapping[option]).join(', ');
    },
    className(): string {
      return buildClass('ui-dropdown', this.modifiers.split(' '));
    },
  },
  watch: {
    value() {
      // Updates current value each time the `value` property is changed.
      this.currentValue = this.value;
      this.focusedOption = findOption(this.value[0])(this.options);
    },
    options() {
      // Avoids having to pass through the entire options array at each rendering.
      this.mapping = this.options.reduce(generateMapping, {});
      if (this.focusedOption >= this.options.length) {
        this.focusedOption = Math.min(0, this.findSiblingOption(this.options.length, -1));
      }
    },
    isDisplayed() {
      // HTML elements with `display: none` can't be focused. Thus, we need to wait for the HTML
      // list to be displayed before actually focusing it.
      if (this.isDisplayed === true) {
        setTimeout(() => {
          this.focusOption(Math.max(0, this.focusedOption));
        }, 50);
      } else {
        (this.$refs.buttonRef as HTMLElement).focus();
      }
    },
  },
  methods: {
    clearOptions(): void {
      this.currentValue = [];
    },
    buildClass(baseClass: string, modifiers: string[]): string {
      return buildClass(baseClass, modifiers);
    },
    focusField(focusedValue?: string): void {
      this.$emit('focus', focusedValue);
    },
    displayList(): void {
      const relativeOffsetTop = (this.$refs.buttonRef as HTMLElement).getBoundingClientRect().top;
      this.position = (relativeOffsetTop > window.innerHeight / 2) ? 'top' : 'bottom';
      this.isDisplayed = true;
    },
    hideList(force = false, event: MouseEvent | null): void {
      // We first ensure that the newly focused element is not an option of the list.
      const focusIsOutsideList = (event !== null)
        ? !(this.$refs.ulRef as Node).contains(event.relatedTarget as Node)
        : true;
      if (focusIsOutsideList && (force === true || this.multiple === false)) {
        this.isDisplayed = false;
      }
    },
    toggleOption(optionIndex: number): void {
      let newValue;
      const selectedValue = this.options[optionIndex].value as string;
      if (this.multiple === false) {
        newValue = [selectedValue];
      } else {
        const selectedIndex = this.currentValue.indexOf(selectedValue);
        newValue = (selectedIndex >= 0)
          ? this.currentValue.slice(0, selectedIndex)
            .concat(this.currentValue.slice(selectedIndex + 1))
          : this.currentValue.concat([selectedValue]);
      }
      this.currentValue = newValue;
      this.$emit('change', newValue);
    },
    selectOption(optionIndex: number): () => void {
      return () => {
        this.focusOption(optionIndex);
        this.toggleOption(optionIndex);
        this.hideList(false, null);
      };
    },
    focusOption(optionIndex: number): void {
      if ((this.$refs.ulRef as Node).childNodes.length > 0) {
        ((this.$refs.ulRef as Node).childNodes[optionIndex] as HTMLElement).focus();
      }
      this.focusedOption = optionIndex;
      this.focusField((this.options[optionIndex] || {}).value);
    },
    navigate(event: KeyboardEvent): void {
      const { key } = event;
      // `event.preventDefault` is not called globally to avoid preventing tabs.
      switch (key) {
        case 'ArrowUp':
          this.focusOption(this.findSiblingOption(this.focusedOption, -1));
          event.preventDefault();
          break;
        case 'ArrowDown':
          this.focusOption(this.findSiblingOption(this.focusedOption, +1));
          event.preventDefault();
          break;
        case 'PageUp':
        case 'Home':
          this.focusOption(Math.max(0, this.findSiblingOption(-1, +1)));
          event.preventDefault();
          break;
        case 'End':
        case 'PageDown':
          this.focusOption(Math.min(this.options.length - 1,
            this.findSiblingOption(this.options.length, -1)));
          event.preventDefault();
          break;
        case ' ':
        case 'Enter':
          if (this.isDisplayed === false) {
            this.isDisplayed = true;
          } else {
            this.selectOption(this.focusedOption)();
          }
          event.preventDefault();
          break;
        case 'Escape':
          this.hideList(true, null);
          event.preventDefault();
          break;
        default:
          break;
      }
    },
    findSiblingOption(startIndex: number, direction: number, offset = 1): number {
      const nextIndex = startIndex + direction * offset;
      if (nextIndex < 0 || nextIndex >= this.options.length) {
        return startIndex;
      }
      const option = this.options[nextIndex];
      return (option.value !== undefined && option.disabled !== true)
        ? nextIndex
        : this.findSiblingOption(startIndex, direction, offset + 1);
    },
    markdown(label: string): string {
      return markdown(label);
    },
  },
});
